import { useRef, useEffect } from 'react';
import videojs from 'video.js';

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function usePrevious(value) {
  var ref = useRef();
  useEffect(function () {
    ref.current = value;
  }, [value]);
  return ref.current;
}

function useVideojs(_ref) {
  var src = _ref.src,
      autoplay = _ref.autoplay,
      onReady = _ref.onReady,
      onPlay = _ref.onPlay,
      onPause = _ref.onPause,
      onTimeUpdate = _ref.onTimeUpdate,
      onSeeking = _ref.onSeeking,
      onSeeked = _ref.onSeeked,
      onEnd = _ref.onEnd,
      className = _ref.className,
      props = _objectWithoutPropertiesLoose(_ref, ["src", "autoplay", "onReady", "onPlay", "onPause", "onTimeUpdate", "onSeeking", "onSeeked", "onEnd", "className"]);

  var vjsId = "vjs-" + Date.now();
  var vjsRef = useRef(null);
  var player;
  var previousSrc = usePrevious(src);
  var previousAutoplay = usePrevious(autoplay);
  useEffect(function () {
    if (previousSrc !== src && !previousSrc) {
      _initPlayer();

      _initPlayerEvents();
    }

    if (previousSrc !== src && previousSrc) {
      _changeSrc();

      _unregisterEvents();

      _initPlayerEvents();
    }

    if (previousAutoplay !== autoplay && previousAutoplay !== undefined) {
      _changeAutoplay();

      _unregisterEvents();

      _initPlayerEvents();
    }
  }, [src, autoplay]);

  var _initPlayer = function _initPlayer() {
    player = videojs(vjsRef.current, props);
    player.src(src);
    player.autoplay(autoplay);
  };

  var _changeSrc = function _changeSrc() {
    player = videojs(vjsRef.current);
    player.src(src);
  };

  var _changeAutoplay = function _changeAutoplay() {
    player = videojs(vjsRef.current);
    player.autoplay(autoplay);
  };

  var _initPlayerEvents = function _initPlayerEvents() {
    var currentTime = 0;
    var previousTime = 0;
    var position = 0;
    player.ready(function () {
      if (onReady) {
        onReady(player);
      }
    });
    player.on('play', function () {
      if (onPlay) {
        onPlay(player.currentTime());
      }
    });
    player.on('pause', function () {
      if (onPause) {
        onPause(player.currentTime());
      }
    });
    player.on('timeupdate', function () {
      if (onTimeUpdate) {
        onTimeUpdate(player.currentTime());
        previousTime = currentTime;
        currentTime = player.currentTime();

        if (previousTime < currentTime) {
          position = previousTime;
          previousTime = currentTime;
        }
      }
    });
    player.on('seeking', function () {
      if (onSeeking) {
        player.off('timeupdate');
        player.one('seeked');
        onSeeking(player.currentTime());
      }
    });
    player.on('seeked', function () {
      if (onSeeked) {
        var completeTime = Math.floor(player.currentTime());
        onSeeked(position, completeTime);
      }
    });
    player.on('ended', function () {
      if (onEnd) {
        onEnd(player.currentTime());
      }
    });
  };

  var _unregisterEvents = function _unregisterEvents() {
    player.off('play');
    player.off('pause');
    player.off('timeupdate');
    player.off('seeking');
    player.off('seeked');
    player.off('ended');
  };

  var videoClassNames = "video-js " + className + " " + (props.bigPlayButtonCentered ? 'vjs-big-play-centered' : '');
  return {
    vjsRef: vjsRef,
    vjsId: vjsId,
    vjsClassName: videoClassNames
  };
}

export { useVideojs };
//# sourceMappingURL=index.modern.js.map
